import HeliviewLogo from '../components/HeliviewLogo'
import { readPlannerLocation, restorableTeamsSearch } from '../lib/plannerLocation'
import { usePlannerLocation } from '../lib/usePlannerLocation'
import MicrosoftSignInButton from '../components/MicrosoftSignInButton'
import { languageOptions } from '../components/LanguageSelect'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { ActionIcon, Alert, Button, Container, Group, Loader, Menu, Select, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { IconCheck, IconChevronDown, IconDots, IconExternalLink, IconLanguage, IconLogout } from '@tabler/icons-react'
import { pages } from '@microsoft/teams-js'
import { teamsContext, teamsRequest, signInToTeams, signInWithTeamsSso } from '../lib/teamsClient'
import { forgetCredential, rememberCredential, rememberedCredential, fromOtherOrganization } from '../lib/teamsCredential'
import TeamsBoard from '../components/TeamsBoard'

export default function TeamsTab({ configure, selected_view_id }) {
  const { t, i18n } = useTranslation()
  const [context, setContext] = useState(null)
  const [token, setToken] = useState(null)
  const [workspace, setWorkspace] = useState(null)
  const [location, updateLocation] = usePlannerLocation()
  const selected = location.view_id || selected_view_id || null
  const setSelected = id => updateLocation({ view_id: id, task_id: '', filters: workspace?.views.find(view => String(view.id) === id)?.filters || {} }, true)
  const [error, setError] = useState('')
  const [needsConsent, setNeedsConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [automaticSignIn, setAutomaticSignIn] = useState(true)
  const view = workspace?.views.find(item => String(item.id) === selected)
  const builtin = workspace?.views.find(item => item.builtin)
  const external = fromOtherOrganization(context, workspace?.user?.microsoft_tenant_id)
  const shared = workspace?.views.filter(item => !item.builtin)
  const viewName = view ? (view.builtin ? t(view.name) : view.name) : t("Kies een view")
  useEffect(() => {
    let current = true
    teamsContext().then(value => { if (current) setContext(value) }).catch(() => { if (current) setError(t("Open Heliview Kanban als app in Microsoft Teams. Via de webapp kun je je views beheren.")) })
    return () => { current = false }
  }, [])
  useEffect(() => {
    if (!context) return
    let current = true
    const resume = async () => {
      const remembered = rememberedCredential(context)
      if (!remembered) return null
      try {
        const data = await teamsRequest('/teams/workspace', remembered)
        if (data.user.microsoft_object_id === context.user?.id) return { credential: remembered, data }
      } catch { /* expired or revoked: fall back to sign-in */ }
      forgetCredential(context)
      return null
    }
    resume().then(async resumed => {
      if (resumed) return resumed
      const credential = await signInWithTeamsSso(context)
      return { credential, data: await teamsRequest('/teams/workspace', credential) }
    }).then(({ credential, data }) => {
      if (!current) return
      rememberCredential(context, credential)
      setToken(credential); setWorkspace(data)
      if (!selected && !configure && data.views.length) updateLocation({ view_id: String(data.views[0].id), filters: data.views[0].filters || {} })
    }).catch(error => {
      if (!current) return
      if (error?.code === 'interaction_required') setNeedsConsent(true)
      else setError(error?.message || t("Microsoft kon niet worden bereikt. Probeer opnieuw."))
    }).finally(() => { if (current) setAutomaticSignIn(false) })
    return () => { current = false }
  }, [context])
  const currentSearch = window.location.search
  useEffect(() => {
    if (!workspace || configure) return
    const key = `heliview.teams.view:${workspace.user.id}:${selected_view_id || 'personal'}`
    try {
      const remembered = restorableTeamsSearch(currentSearch, localStorage.getItem(key), workspace.views.map(view => String(view.id)))
      if (remembered) updateLocation(readPlannerLocation(remembered))
      else localStorage.setItem(key, currentSearch)
    } catch { /* URL state still works when Teams blocks browser storage. */ }
  }, [workspace, configure, selected_view_id, currentSearch])
  useEffect(() => {
    if (!context || !configure) return
    pages.config.setValidityState(Boolean(view))
    pages.config.registerOnSaveHandler(async event => {
      if (!view) { event.notifyFailure(t("Kies een view")); return }
      try {
        await pages.config.setConfig({ entityId: `view:${view.id}`, contentUrl: `${window.location.origin}/teams/tab?view_id=${view.id}`, websiteUrl: `${window.location.origin}/?view_id=${view.id}`, suggestedDisplayName: view.builtin ? t(view.name) : view.name })
        event.notifySuccess()
      } catch { event.notifyFailure(t("De tab kon niet worden opgeslagen. Probeer opnieuw.")) }
    })
    return () => pages.config.setValidityState(false)
  }, [context, configure, view, i18n.language])
  async function signIn(editing = false) {
    setBusy(true); setError(''); setNeedsConsent(false)
    try {
      const credential = await signInToTeams(context, editing === true)
      const data = await teamsRequest('/teams/workspace', credential)
      rememberCredential(context, credential)
      setToken(credential); setWorkspace(data)
      if (!selected && !configure && data.views.length) updateLocation({ view_id: String(data.views[0].id), filters: data.views[0].filters || {} })
    } catch (e) { setError(typeof e === 'string' ? e : e.message || t("Microsoft-aanmelding is niet gelukt.")) }
    finally { setBusy(false) }
  }
  async function signOut() {
    setBusy(true); setError('')
    try { await teamsRequest('/teams/session', token, 'DELETE'); forgetCredential(context); setToken(null); setWorkspace(null) }
    catch (e) { setError(e.message) }
    finally { setBusy(false) }
  }
  return <Container fluid className="teams-tab" py="md"><Stack gap="md">
    <Group justify="space-between" wrap="nowrap" className="teams-header">
      {configure || !workspace ? <Group gap="xs" wrap="nowrap"><HeliviewLogo size={26}/><Title order={1} className="workspace-title">{configure ? t("Heliview Kanban toevoegen") : 'Heliview Kanban'}</Title></Group>
        : <Menu position="bottom-start" withinPortal><Menu.Target><UnstyledButton className="teams-view-title" aria-label={view?.builtin ? t("Mijn taken") : t("Gedeelde view")}><Title order={1} className="workspace-title">{viewName}</Title><IconChevronDown size={18}/></UnstyledButton></Menu.Target>
            <Menu.Dropdown>{builtin && <Menu.Item key={builtin.id} onClick={() => setSelected(String(builtin.id))} rightSection={String(builtin.id) === selected ? <IconCheck size={14}/> : null}>{t(builtin.name)}</Menu.Item>}<Menu.Label>{t("Gedeelde view")}</Menu.Label>{shared.map(item => <Menu.Item key={item.id} onClick={() => setSelected(String(item.id))} rightSection={String(item.id) === selected ? <IconCheck size={14}/> : null}>{item.name}</Menu.Item>)}</Menu.Dropdown></Menu>}
      <Menu position="bottom-end" width={240} withinPortal><Menu.Target><ActionIcon variant="subtle" color="gray" aria-label={t("Meer opties")}><IconDots size={18}/></ActionIcon></Menu.Target>
        <Menu.Dropdown>
          <Menu.Sub position="left-start"><Menu.Sub.Target><Menu.Sub.Item leftSection={<IconLanguage size={16}/>} rightSection={<Text size="xs" c="dimmed">{languageOptions.find(option => option.value === i18n.language)?.label}</Text>}>{t("Taal")}</Menu.Sub.Item></Menu.Sub.Target>
            <Menu.Sub.Dropdown>{languageOptions.map(option => <Menu.Item key={option.value} onClick={() => i18n.changeLanguage(option.value)} rightSection={option.value === i18n.language ? <IconCheck size={14}/> : null}>{option.label}</Menu.Item>)}</Menu.Sub.Dropdown></Menu.Sub>
          <Menu.Item component="a" href={`/${window.location.search}`} target="_blank" rel="noopener noreferrer" leftSection={<IconExternalLink size={16}/>}>{t("Open webapp")}</Menu.Item>
          {token && <><Menu.Divider/><Menu.Item leftSection={<IconLogout size={16}/>} onClick={signOut} disabled={busy}>{t("Uitloggen")}</Menu.Item></>}
        </Menu.Dropdown></Menu>
    </Group>
    {error && <Alert color="red">{t(error)}</Alert>}
    {needsConsent && <Alert color="gray">{t("Verbind je Microsoft-account om Planner te openen.")}</Alert>}
    {!context && !error && <Loader size="sm"/>}
    {context && !token && automaticSignIn && <Group><Loader size="sm"/><Text>{t("Aanmelden via Teams…")}</Text></Group>}
    {context && !token && !automaticSignIn && <><Text>{t("Meld aan met je Microsoft-werkaccount. Iedere gebruiker ziet alleen Planner-data waartoe die zelf toegang heeft.")}</Text><MicrosoftSignInButton onClick={signIn} loading={busy}/></>}
    {workspace && <>
      {workspace.billing_required && <Alert color="gray">{t("Je organisatie heeft momenteel geen toegang tot Heliview. Neem contact op met de appbeheerder.")}</Alert>}
      {configure && <Select label={t("View")} placeholder={t("Kies een view")} data={workspace.views.map(item => ({ value: String(item.id), label: item.builtin ? t(item.name) : item.name }))} value={selected} onChange={setSelected}/>}
      {!configure && external && <Alert color="gray">{t("Deze tab hoort bij een andere organisatie. Heliview toont alleen Planner-taken uit je eigen organisatie. Kies Mijn taken om je eigen taken te zien.")}</Alert>}
      {selected && !view && !external && <Alert color="yellow">{t("De ingestelde view is niet meer beschikbaar. Kies een andere view.")}</Alert>}
      {configure ? <Text size="sm">{t("Kies Mijn taken of een gedeelde view en klik op Opslaan in Teams. Mijn taken toont voor iedere gebruiker diens eigen taken.")}</Text> : view && !workspace.billing_required && <TeamsBoard key={view.id} view={view} user={workspace.user} token={token} editingAllowed={workspace.planner_editing} writeConnected={workspace.write_connected} connectWrite={() => signIn(true)}/>}
    </>}
  </Stack></Container>
}
